import type { Media, Task } from 'src/services/apollo/generated/graphql'

// Shape IDs use crypto-random alphanumeric strings
export type ShapeId = string & { __brand: 'ShapeId' }
export type AssetId = string & { __brand: 'AssetId' }

export type ToolId =
  | 'hand'
  | 'select'
  | 'marker'
  | 'marker-geo'
  | 'marker-lasso'
  | 'marker-eraser'
  | 'outpaint'
  | 'crop'

export type MarkerLineCap = 'round' | 'butt'

export type SelectSubState = 'idle' | 'dragging' | 'resizing'

export interface Point {
  x: number
  y: number
}

export interface Size {
  width: number
  height: number
}

export interface Box {
  x: number
  y: number
  width: number
  height: number
}

// Asset stored in the editor (image data)
export interface EditorAsset {
  id: AssetId
  name: string
  src: string
  width: number
  height: number
  mimeType: string
  meta: {
    mediaId?: string
    media?: Media
    originalWidth?: number
    originalHeight?: number
  }
}

// Shape metadata - mirroring tldraw's meta conventions
export interface ShapeMeta {
  pixai_isBaseImage?: boolean
  pixai_outpaintFrameOf?: ShapeId
  inpaintOfShape?: ShapeId
  generationTask?: Task | null
  isPaint?: boolean
  submittedAt?: number
}

// All shape types in the editor
export type EditorShapeType =
  | 'image'
  | 'genmedia'
  | 'marker'
  | 'marker-geo'
  | 'marker-lasso'
  | 'frame'

// Base shape definition
interface BaseShape {
  id: ShapeId
  type: EditorShapeType
  x: number
  y: number
  parentId?: ShapeId
  index: string // fractional index for z-ordering
  opacity: number
  rotation: number
  isLocked: boolean
  meta: ShapeMeta
}

// Image shape (base image uploaded from file)
export interface ImageShape extends BaseShape {
  type: 'image'
  props: {
    w: number
    h: number
    assetId: AssetId | null
  }
}

// GenMedia shape (generated images with task tracking)
export interface GenMediaShape extends BaseShape {
  type: 'genmedia'
  props: {
    w: number
    h: number
    currentAssetId: AssetId | null
    assetIds: AssetId[]
    prompts: string | null
    pending: string | null
    alert?: string | null
    shouldReceiveMarker?: boolean
    isInpaint?: boolean
    derivationOfShape?: ShapeId
    crop?: {
      topLeft: Point
      bottomRight: Point
    }
  }
}

// Free-hand marker stroke
export interface MarkerShape extends BaseShape {
  type: 'marker'
  props: {
    points: number[] // flat array [x1,y1,x2,y2,...]
    color: string
    width: number
    isPaint: boolean
    lineCap?: MarkerLineCap
    isEraser?: boolean
  }
}

// Rectangle selection
export interface MarkerGeoShape extends BaseShape {
  type: 'marker-geo'
  props: {
    x: number
    y: number
    w: number
    h: number
    color: string
    isPaint: boolean
  }
}

// Lasso selection
export interface MarkerLassoShape extends BaseShape {
  type: 'marker-lasso'
  props: {
    points: number[] // flat array [x1,y1,x2,y2,...]
    color: string
    isPaint: boolean
  }
}

// Outpaint frame (dashed rect)
export interface FrameShape extends BaseShape {
  type: 'frame'
  props: {
    w: number
    h: number
  }
}

export type EditorShape =
  | ImageShape
  | GenMediaShape
  | MarkerShape
  | MarkerGeoShape
  | MarkerLassoShape
  | FrameShape

export type InpaintShape = MarkerShape | MarkerGeoShape | MarkerLassoShape
export type ImageShapes = GenMediaShape | ImageShape

// Camera state
export interface Camera {
  x: number
  y: number
  zoom: number
}

// Tool style state
export interface ToolStyles {
  markerColor: string
  paintColor: string
  markerWidth: number
  markerIsPaint: boolean
  markerLineCap: MarkerLineCap
  markerOpacity: number
  paintOpacity: number
}

export const DEFAULT_TOOL_STYLES: ToolStyles = {
  markerColor: '#ff0000',
  paintColor: '#4a90d9',
  markerWidth: 40,
  markerIsPaint: false,
  markerLineCap: 'round',
  markerOpacity: 0.5,
  paintOpacity: 1,
}

export const MIN_ZOOM = 0.1
export const MAX_ZOOM = 8
export const DEFAULT_GRID_SIZE = 8
